import React, { useEffect, useMemo } from "react";
import { SectionProps } from "../sections/SectionProps";
import {
  PublicEnquiryModel,
  PublicEnquiryStatus,
} from "../../domain/PublicEnquiry";
import { ItemState } from "../../redux/state";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../../redux/selectors";
import {
  modelToJsonPatchDocument,
  getLookupTracker,
  buildMetaData,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { ItemDetails } from "./ItemDetails";
import { Prompt } from "react-router-dom";
import { ModuleAppBar } from "../ModuleAppBar";
import {
  AppBarConfirmButton,
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  reopenPublicEnquiry,
  updatingItem,
  setPendingLines,
  deleteSelectedAttachment,
} from "../../redux/actions";
import { LockOpen, Assignment } from "@material-ui/icons";

import { GuidanceDrawer } from "@ofqual/portal-components/components/Drawer";
import { guidanceService, publicEnquiryService } from "../../services";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { UserType } from "../../domain/User";
import { Attachment } from "../../domain/Attachment";

type NonNullableItemState = ItemState & {
  item: Exclude<ItemState["item"], null>;
};

export const ItemForm: React.FC<{
  state: NonNullableItemState;
  handleDrawerToggle: (isOpen: boolean) => void;
  open: boolean;
}> = ({
  state: { item, status, lastCrudResult, attachmentsToDelete },
  handleDrawerToggle,
  open,
}) => {
    const {
      handleSubmit,
      control,
      formState,
      errors,
      setValue,
      setError,
      clearErrors,
      getValues,
      reset,
    } = useForm<PublicEnquiryModel>({
      defaultValues: item,
      shouldUnregister: false,
    });

    const dispatch = useDispatch();
    const { token, experimentalFeatures, userType } = useAuth(authSelector);

    // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
    useEffect(() => {
      reset(item);
    }, [reset, item]);

    const isClosed = item.status === PublicEnquiryStatus.Closed;

    const disabled =
      status === "UPDATING";

    const isNotPendingLines =
      userType === UserType.Ofqual &&
      (item.status === PublicEnquiryStatus.Open ||
        item.status === PublicEnquiryStatus.New);
    const isPendingLines =
      userType === UserType.Ofqual &&
      item.status === PublicEnquiryStatus["Pending Lines"];

    console.log(
      "pending lines status's ",
      isNotPendingLines,
      isPendingLines,
      isPendingLines || isNotPendingLines
    );

    // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
    const { dirtyFields } = formState;
    const lookupTracker = useMemo(() => getLookupTracker(), []);

    const onSubmit = (newModel: PublicEnquiryModel) => {


      const fieldsToIgnore: (keyof PublicEnquiryModel)[] = ["supportingFiles"];

      const changedFields = (Object.keys(dirtyFields) as (keyof PublicEnquiryModel)[]).filter(
        (x) => !fieldsToIgnore.includes(x));

      let document = modelToJsonPatchDocument(newModel, changedFields);
      const resourceTagPatch = document.find(
        (patch) => patch.path === "/resourceTags"
      );
      // let resourceTagIds: number[] = item.resourceTags;
      if (resourceTagPatch) {
        // resourceTagIds = resourceTagPatch.value;
        document = document.filter((x) => x !== resourceTagPatch);
      }
      const meta = buildMetaData(newModel, item, changedFields, lookupTracker);
      dispatch(
        updatingItem(item.id, { document, meta }, attachmentsToDelete, newModel.supportingFiles
        )
      );
    };


    const deleteAttachment = (attachment: Attachment) => {
      dispatch(deleteSelectedAttachment(attachment));
    };

    const downloadAttachment = (blobId: string): Promise<Blob> => {
      return publicEnquiryService.downloadSupportingDocument(item!.id, blobId, { token });
    };




    const sectionProps: SectionProps<PublicEnquiryModel> = {
      isCreateMode: false,
      disabled,
      control,
      errors,
      formState,
      item,
      clearErrors,
      setValue: (name, value) => setValue(name, value),
      getValues,
      registerCustomError: (name: string, isError: boolean) => {
        const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
        if (isError && !isCurrentlyARegisteredError) {
          setError(name, {});
        }
        if (!isError && isCurrentlyARegisteredError) {
          clearErrors(name);
        }
      },
      lookupTracker,
      deleteAttachment,
      downloadAttachment,
    };

    const showReOpened = isClosed;

    const mappedGuidanceService = useMemo(
      () => ({
        get: () => {
          return guidanceService.get(
            { module: "publicEnquiry", key: "edit-publicEnquiry" },
            { token }
          );
        },
      }),
      [token]
    );

    return (
      <>
        <Prompt
          message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
          when={Object.keys(formState.dirtyFields).length > 0}
        />
        <ModuleAppBar
          breadcrumbs={[
            { label: "List", to: "List" },
            { label: `${item.referenceNumber} ${item.title}` },
          ]}
        >
          {isNotPendingLines ? (
            <>
              <AppBarConfirmButton
                tooltip="Sent a pending lines message"
                Icon={Assignment}
                onClick={() => dispatch(setPendingLines(item.id, true))}
                dataTest="set-pending-lines"
                text="Set pending"
              >
                Are you sure you want set the status of this public enquiry to pending
                lines?
              </AppBarConfirmButton>
            </>
          ) : null}

          {isPendingLines ? (
            <>
              <AppBarConfirmButton
                tooltip={"Received valid response to a pending lines message"}
                Icon={Assignment}
                onClick={() => dispatch(setPendingLines(item.id, false))}
                dataTest="reset-pending-lines"
                text="Reset Pending"
              >
                Are you sure you want to reset the status of this public enquiry back
                from pending lines?
              </AppBarConfirmButton>
            </>
          ) : null}

          {showReOpened ? (
            <>
              <AppBarConfirmButton
                tooltip="Re-Open"
                Icon={LockOpen}
                onClick={() => dispatch(reopenPublicEnquiry(item.id))}
                dataTest="open-pe"
                text="Re-Open"
              >
                Are you sure you want to re-open this public enquiry?
              </AppBarConfirmButton>
            </>
          ) : null}
          <AppBarValidationIndicator errors={errors} />
          <AppBarSaveButton
            onClick={handleSubmit(onSubmit)}
            isSubmitting={status === "UPDATING"}
            isSubmitted={lastCrudResult.isSuccess}
            dataTest="save"
            disabled={disabled}
          />

          <ExperimentalFeature
            experimentalModuleName="portal-public-enquiry"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="publicEnquiryGuidancecomplete"
          >
            <GuidanceDrawer
              open={open}
              handleDrawerToggle={handleDrawerToggle}
              service={mappedGuidanceService}
            />
          </ExperimentalFeature>
        </ModuleAppBar>
        <form>
          <ItemDetails sectionProps={sectionProps} />
        </form>
      </>
    );
  };
